import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import Skeleton from "../components/common/skeleton";
import StaleBanner from "../components/paper/StaleBanner";
import PositionsTable from "../components/paper/PositionsTable";
import TradesTable from "../components/paper/TradesTable";

import RetireModal from "../components/paper/RetireModal";
import {
    fetchPaperSummary,
    fetchPaperStrategyPositions,
    fetchPaperStrategyTrades,
    fetchPaperRetired,
    retirePaperStrategy,
    fetchPaperAdopted,
    adoptPaperStrategy,
    unadoptPaperStrategy,
    fetchPaperFirstSeen,
} from "../services/paperApi";
import { NewBadge, isRecentlyAdded } from "../components/paper/PaperStrategyCard";
import { getStrategyMeta } from "../data/strategyMeta";
import {
    PAPER_INITIAL_CAPITAL_KRW,
    CAPITAL_BASELINE_PCT,
    computeCapital,
} from "../data/paperCapital";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import { formatKst, formatLabeledAt } from "../utils/time";

const POLL_INTERVAL_MS = 5_000;
const STALE_FAIL_THRESHOLD = 3;
const TRADES_LIMIT = 100;

/**
 * PaperStrategyDetailPage — /research/strategies/:id.
 *
 * 한 전략의 활성 포지션 + 최근 거래 + 요약 KPI 를 5 초 주기로 폴링.
 * paper-trader 의 /api/strategy/{id}/equity 는 DAS-121 화이트리스트에
 * 포함하지 않았으므로 디테일 차트는 향후 확장으로 미룬다.
 */
class PaperStrategyDetailPageInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: true,
            stratSummary: null,
            positions: [],
            trades: [],
            failureCount: 0,
            stale: false,
            lastUpdated: null,
            // 메타 설명 카드는 기본 펼침 — 상세 진입 시 전략 설명을 바로 보여준다.
            // (사용자가 원하면 접을 수 있음)
            descriptionExpanded: true,
            descriptionAutoExpandedFor: null,
            // DAS-126 — 백엔드 폐기 레코드 (이 전략이 폐기됐으면 객체, 아니면 null)
            retiredRecord: null,
            showRetireModal: false,
            retireSubmitting: false,
            retireError: null,
            retireStopWarning: null, // 폐기는 됐으나 paper-trader 중지 실패 시 경고
            // DAS-126 — 백엔드 채택 레코드 (이 전략이 채택됐으면 객체, 아니면 null)
            adoptedRecord: null,
            adoptSubmitting: false, // 채택/채택해제 API 진행 중 여부
            showAdoptModal: false,  // 채택 사유 입력 모달
            isNew: false, // 최근 3일 내 추가된 전략(신규 배지) — first-seen 기반
        };
        this.shouldPoll = true;
        this.isPolling = false;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        this.pollData();
    }

    componentDidUpdate(prevProps) {
        // :id 가 바뀌면 (다른 전략 디테일로 navigate) 옛 데이터를 즉시 비우고
        // 새 id 로 폴링을 다시 시작한다. 그렇지 않으면 다음 5 초 까지 옛 전략의
        // positions/trades 가 잘못 표시된다.
        if (prevProps.id !== this.props.id) {
            clearTimeout(this.pollTimer);
            this.setState(
                {
                    loading: true,
                    stratSummary: null,
                    positions: [],
                    trades: [],
                    failureCount: 0,
                    stale: false,
                    lastUpdated: null,
                    // 폐기/채택 여부는 백엔드 레코드 도착 후 결정 — 일단 초기화
                    retiredRecord: null,
                    adoptedRecord: null,
                    descriptionExpanded: false,
                    descriptionAutoExpandedFor: null,
                    showRetireModal: false,
                    retireError: null,
                },
                () => this.pollData(),
            );
        }
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        const { id } = this.props;
        if (!id) return;
        this.isPolling = true;

        Promise.allSettled([
            fetchPaperSummary(),
            fetchPaperStrategyPositions(id),
            fetchPaperStrategyTrades(id, TRADES_LIMIT),
            fetchPaperRetired(),
            fetchPaperAdopted(),
            fetchPaperFirstSeen(),
        ])
            .then(([sumRes, posRes, tradeRes, retiredRes, adoptedRes, firstSeenRes]) => {
                if (!this.shouldPoll) return;
                const allStrats = sumRes.status === "fulfilled"
                    ? (sumRes.value.data?.strategies ?? [])
                    : [];
                const stratSummary = allStrats.find(
                    (s) => (s.Strategy ?? s.strategy) === id,
                );
                const positions =
                    posRes.status === "fulfilled" ? posRes.value.data : [];
                const trades =
                    tradeRes.status === "fulfilled" ? tradeRes.value.data : [];
                // 폐기 레코드 조회 (실패 시 직전 값 유지). stale 판정에서는 제외.
                // 폐기 확정 진행 중(retireSubmitting)에 늦게 도착한 in-flight poll 이
                // upsert 커밋 이전 목록으로 retiredRecord 를 null 로 되돌려 뷰가
                // 깜빡이는 경합을 막기 위해, 제출 중에는 갱신을 건너뛴다.
                let retiredRecord = this.state.retiredRecord;
                if (retiredRes.status === "fulfilled" && !this.state.retireSubmitting) {
                    const list = Array.isArray(retiredRes.value.data)
                        ? retiredRes.value.data
                        : [];
                    retiredRecord = list.find((r) => r.strategy_id === id) ?? null;
                }
                // 채택 레코드 조회 (retiredRecord 와 동일한 경합 방지 — 제출 중 스킵).
                let adoptedRecord = this.state.adoptedRecord;
                if (adoptedRes.status === "fulfilled" && !this.state.adoptSubmitting) {
                    const list = Array.isArray(adoptedRes.value.data)
                        ? adoptedRes.value.data
                        : [];
                    adoptedRecord = list.find((r) => r.strategy_id === id) ?? null;
                }
                // first-seen — 이 전략이 최근 3일 내 추가됐는지(신규 배지). 실패 시 직전 유지.
                let isNew = this.state.isNew;
                if (firstSeenRes.status === "fulfilled" && Array.isArray(firstSeenRes.value.data)) {
                    const rec = firstSeenRes.value.data.find((r) => r.strategy_id === id);
                    isNew = rec ? isRecentlyAdded(rec.first_seen_at) : false;
                }
                const anyFailed = [sumRes, posRes, tradeRes].some(
                    (r) => r.status === "rejected",
                );
                const failureCount = anyFailed ? this.state.failureCount + 1 : 0;
                // 폐기 전략은 사유/finalStats 가 핵심 정보 — 레코드 첫 도착 시 자동 펼침.
                const autoExpand =
                    retiredRecord && this.state.descriptionAutoExpandedFor !== id;
                this.setState({
                    stratSummary,
                    positions: Array.isArray(positions) ? positions : [],
                    trades: Array.isArray(trades) ? trades : [],
                    retiredRecord,
                    adoptedRecord,
                    isNew,
                    ...(autoExpand
                        ? {
                              descriptionExpanded: true,
                              descriptionAutoExpandedFor: id,
                          }
                        : {}),
                    failureCount,
                    stale: failureCount >= STALE_FAIL_THRESHOLD,
                    lastUpdated: anyFailed
                        ? this.state.lastUpdated
                        : new Date(),
                    loading: false,
                });
            })
            .finally(() => {
                this.isPolling = false;
                if (this.shouldPoll)
                    this.pollTimer = setTimeout(this.pollData, POLL_INTERVAL_MS);
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onBack = () => {
        if (this.props.navigate) this.props.navigate("/research");
    };

    openRetireModal = () =>
        this.setState({ showRetireModal: true, retireError: null });

    closeRetireModal = () => {
        if (this.state.retireSubmitting) return;
        this.setState({ showRetireModal: false, retireError: null });
    };

    /**
     * 폐기 확정 — 백엔드에 폐기 레코드를 upsert 하고, 성공 시 폐기 레코드를
     * 재조회해 즉시 폐기 뷰로 전환한다. (DAS-126)
     */
    onConfirmRetire = (reason, finalStats) => {
        const { id } = this.props;
        this.setState({ retireSubmitting: true, retireError: null });
        let stopWarning = null;
        retirePaperStrategy(id, reason, finalStats)
            .then((res) => {
                // paper-trader 전략 중지 실패 시 경고 (폐기 분류는 성공해도 실제 매매가
                // 계속될 수 있으므로 사용자에게 알림).
                if (res?.data?.paper_stopped === false) {
                    stopWarning =
                        res.data.paper_error ||
                        "paper-trader 전략 중지에 실패했습니다. 실제 매매가 계속될 수 있어 재시도가 필요합니다.";
                }
                return fetchPaperRetired();
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const retiredRecord = list.find((r) => r.strategy_id === id) ?? null;
                this.setState({
                    retiredRecord,
                    showRetireModal: false,
                    retireSubmitting: false,
                    descriptionExpanded: true,
                    descriptionAutoExpandedFor: id,
                    retireStopWarning: stopWarning,
                });
                // 다음 주기를 기다리지 않고 즉시 재동기화 (summary/positions 등).
                clearTimeout(this.pollTimer);
                this.pollData();
            })
            .catch((err) => {
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    "알 수 없는 오류";
                this.setState({ retireSubmitting: false, retireError: msg });
            });
    };

    /**
     * 채택 — 매매를 중지하지 않고(paper-trader 호출 없음) 우수 전략으로 북마크한다.
     * 채택 사유 입력 모달(AdoptModal)을 열어 사유를 받은 뒤 확정한다. (DAS-126)
     */
    onAdopt = () => {
        if (this.state.adoptSubmitting) return;
        this.setState({ showAdoptModal: true });
    };

    onConfirmAdopt = (reason) => {
        const { id } = this.props;
        this.setState({ showAdoptModal: false, adoptSubmitting: true });
        adoptPaperStrategy(id, reason, null)
            .then(() => fetchPaperAdopted())
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const adoptedRecord = list.find((r) => r.strategy_id === id) ?? null;
                this.setState({ adoptedRecord, adoptSubmitting: false });
            })
            .catch(() => {
                // 실패 시 다음 폴링이 상태를 다시 맞춘다.
                this.setState({ adoptSubmitting: false });
            });
    };

    onCancelAdopt = () => {
        this.setState({ showAdoptModal: false });
    };

    /**
     * 채택 해제 — 북마크만 제거하며 매매에는 영향이 없다(paper-trader 호출 없음).
     */
    onUnadopt = () => {
        const { id } = this.props;
        if (this.state.adoptSubmitting) return;
        this.setState({ adoptSubmitting: true });
        unadoptPaperStrategy(id)
            .then(() => fetchPaperAdopted())
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const adoptedRecord = list.find((r) => r.strategy_id === id) ?? null;
                this.setState({ adoptedRecord, adoptSubmitting: false });
            })
            .catch(() => {
                this.setState({ adoptSubmitting: false });
            });
    };

    render() {
        const { id } = this.props;
        const {
            collapsed,
            loading,
            stratSummary,
            positions,
            trades,
            stale,
            lastUpdated,
        } = this.state;

        // 설명(strategyMeta) + 백엔드 폐기 레코드 머지 → 기존 meta.retired* 렌더 유지.
        const { retiredRecord } = this.state;
        const baseMeta = getStrategyMeta(id);
        const meta = retiredRecord
            ? {
                  ...(baseMeta ?? {}),
                  retired: true,
                  retiredAt: formatLabeledAt(retiredRecord.labeled_at),
                  retiredReason: retiredRecord.reason,
                  finalStats: retiredRecord.final_stats ?? null,
              }
            : baseMeta;
        const isRetired = !!retiredRecord;
        const isAdopted = !!this.state.adoptedRecord;
        const pnl = stratSummary?.PnLKRW ?? stratSummary?.pnl_krw ?? 0;
        const trades_n =
            stratSummary?.Trades ?? stratSummary?.trades ?? trades.length;
        const wr = stratSummary?.WinRate ?? stratSummary?.win_rate ?? 0;
        // paper-trader 응답의 실제 키는 profit_factor.
        const pf =
            stratSummary?.profit_factor ??
            stratSummary?.ProfitFct ??
            stratSummary?.profit_fct ??
            -1;

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar
                    activeMenu="research"
                    collapsed={collapsed}
                    onCollapse={this.onCollapse}
                />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed
                            ? "sm:pl-[88px] lg:pl-[96px]"
                            : "sm:pl-[252px] lg:pl-[260px]"
                    } px-4 py-4 sm:pr-8 sm:py-6 lg:pr-10`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <button
                        type="button"
                        onClick={this.onBack}
                        aria-label="Research 목록으로 돌아가기"
                        className="inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content mb-3 min-h-11 -ml-1 px-1"
                    >
                        ← Research 로
                    </button>
                    <header className="mb-6">
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl font-bold text-content">{id}</h1>
                            {this.state.isNew && <NewBadge />}
                            {meta?.name && (
                                <span className="text-base text-content-secondary">
                                    — {meta.name}
                                </span>
                            )}
                            {stratSummary && (
                                <StatusBadge
                                    status={resolveStatus({
                                        openCount: stratSummary.OpenCount ?? stratSummary.open_count ?? 0,
                                        lastTradeTs: stratSummary.last_trade_ts ?? stratSummary.LastTradeTs,
                                        trades: stratSummary.Trades ?? stratSummary.trades ?? 0,
                                    })}
                                />
                            )}
                            {meta?.retired && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-loss px-2 py-1 bg-loss-subtle rounded border border-loss/30">
                                    <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-loss" />
                                    폐기됨
                                </span>
                            )}
                            {isAdopted && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-warning px-2 py-1 bg-surface-elevated rounded border border-warning/30">
                                    <span aria-hidden="true">⭐</span>
                                    채택됨
                                </span>
                            )}
                            <div className="ml-auto flex items-center gap-2">
                                {/* 채택 버튼 — 폐기된 전략에는 숨김. 이미 채택이면 "채택 해제" 표시. */}
                                {!isRetired &&
                                    (isAdopted ? (
                                        <button
                                            type="button"
                                            onClick={this.onUnadopt}
                                            disabled={this.state.adoptSubmitting}
                                            className="inline-flex items-center gap-1.5 text-xs text-content-secondary border border-surface-border rounded px-2.5 py-1.5 min-h-11 hover:bg-surface-hover disabled:opacity-50 transition-colors"
                                        >
                                            <span aria-hidden="true">⭐</span>
                                            {this.state.adoptSubmitting ? "처리 중…" : "채택 해제"}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={this.onAdopt}
                                            disabled={this.state.adoptSubmitting}
                                            className="inline-flex items-center gap-1.5 text-xs text-warning border border-warning/40 rounded px-2.5 py-1.5 min-h-11 hover:bg-warning-subtle disabled:opacity-50 transition-colors"
                                        >
                                            <span aria-hidden="true">⭐</span>
                                            {this.state.adoptSubmitting ? "처리 중…" : "채택"}
                                        </button>
                                    ))}
                                {!isRetired && (
                                    <button
                                        type="button"
                                        onClick={this.openRetireModal}
                                        className="inline-flex items-center gap-1.5 text-xs text-loss border border-loss/40 rounded px-2.5 py-1.5 min-h-11 hover:bg-loss-subtle transition-colors"
                                    >
                                        <span aria-hidden="true">⊘</span>
                                        폐기
                                    </button>
                                )}
                            </div>
                        </div>
                        <p className="text-sm text-content-secondary mt-1">
                            카테고리: {meta?.paradigm ?? stratSummary?.category ?? "—"}
                            <span className="mx-2 text-content-muted">·</span>
                            운영{" "}
                            <span className="text-content">
                                {formatUptime(stratSummary?.first_trade_ts ?? stratSummary?.FirstTradeTs)}
                            </span>
                        </p>
                        <div className="text-xs text-content-muted mt-2">
                            마지막 갱신: {lastUpdated ? formatKst(lastUpdated) : "—"}
                        </div>
                    </header>

                    {this.state.retireStopWarning && (
                        <div
                            role="alert"
                            className="flex items-start gap-2 text-xs text-warning bg-warning-subtle border border-warning/30 rounded px-3 py-2 mb-3"
                        >
                            <span aria-hidden="true">⚠</span>
                            <span className="min-w-0">
                                폐기 처리는 완료됐으나 paper-trader 전략 중지에 실패했습니다:{" "}
                                {this.state.retireStopWarning} — 잠시 후 다시 폐기를
                                시도하거나 paper-trader 상태를 확인하세요.
                            </span>
                            <button
                                type="button"
                                onClick={() => this.setState({ retireStopWarning: null })}
                                aria-label="경고 닫기"
                                className="ml-auto text-content-muted hover:text-content shrink-0"
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    <StaleBanner
                        stale={stale}
                        lastUpdated={lastUpdated}
                        onRetry={this.pollData}
                    />

                    {loading && !stratSummary ? (
                        <>
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                {[0, 1, 2, 3].map((i) => (
                                    <Skeleton key={i} variant="card" height="72px" />
                                ))}
                            </section>
                            <Skeleton variant="card" height="180px" className="mb-6" />
                            <Skeleton variant="card" height="240px" />
                        </>
                    ) : (
                        <>
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 sticky top-12 z-20 bg-surface -mx-4 px-4 py-2 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
                                <KpiCard
                                    label="PnL (KRW)"
                                    value={Math.round(pnl).toLocaleString()}
                                    tone={pnl > 0 ? "gain" : pnl < 0 ? "loss" : "neutral"}
                                />
                                <KpiCard label="거래" value={String(trades_n)} />
                                <KpiCard
                                    label="WR"
                                    value={`${(wr * 100).toFixed(1)}%`}
                                />
                                <KpiCard
                                    label="PF"
                                    value={pf >= 0 ? pf.toFixed(2) : "—"}
                                />
                            </section>

                            <CapitalPanel
                                pnlKrw={pnl}
                                initialCapital={meta?.initialCapital}
                            />

                            <div className="mb-6">
                                <button
                                    type="button"
                                    onClick={() =>
                                        this.setState((s) => ({
                                            descriptionExpanded: !s.descriptionExpanded,
                                        }))
                                    }
                                    aria-expanded={this.state.descriptionExpanded}
                                    aria-controls="strategy-description-card"
                                    className="w-full inline-flex items-center justify-between gap-2 text-xs text-content-secondary hover:text-content bg-surface-card border border-surface-border rounded px-3 py-2 min-h-11 transition-colors"
                                >
                                    <span>
                                        전략 요약{" "}
                                        {this.state.descriptionExpanded ? "접기" : "보기"}
                                        {meta?.paradigm && (
                                            <span className="ml-2 text-content-muted">
                                                ({meta.paradigm})
                                            </span>
                                        )}
                                    </span>
                                    <span aria-hidden="true">
                                        {this.state.descriptionExpanded ? "▲" : "▼"}
                                    </span>
                                </button>
                                {this.state.descriptionExpanded && (
                                    <div id="strategy-description-card" className="mt-2">
                                        <StrategyDescriptionCard meta={meta} id={id} />
                                    </div>
                                )}
                            </div>

                            <section className="bg-surface-card border border-surface-border rounded-lg p-4 mb-6">
                                <h2 className="text-base font-semibold text-content mb-3">
                                    활성 포지션 ({positions.length})
                                </h2>
                                <PositionsTable positions={positions} />
                            </section>

                            <section className="bg-surface-card border border-surface-border rounded-lg p-4">
                                <h2 className="text-base font-semibold text-content mb-3">
                                    최근 거래 ({trades.length}/{TRADES_LIMIT})
                                </h2>
                                <TradesTable trades={trades} />
                            </section>
                        </>
                    )}

                    <ScrollToTopButton />
                </main>

                {this.state.showRetireModal && !isRetired && (
                    <RetireModal
                        strategy={stratSummary ?? { strategy: id }}
                        submitting={this.state.retireSubmitting}
                        error={this.state.retireError}
                        onConfirm={this.onConfirmRetire}
                        onCancel={this.closeRetireModal}
                    />
                )}

                {this.state.showAdoptModal && (
                    <AdoptModal
                        id={id}
                        onConfirm={this.onConfirmAdopt}
                        onCancel={this.onCancelAdopt}
                    />
                )}
            </div>
        );
    }
}

const IDLE_WARN_HOURS = 24;

function StatusBadge({ status }) {
    const dotClass = {
        brand: "bg-brand animate-pulse",
        gain: "bg-gain",
        warning: "bg-warning",
        muted: "bg-content-muted",
    }[status.tone];
    return (
        <span
            role="status"
            aria-label={status.title}
            className="inline-flex items-center gap-1.5 text-xs text-content-secondary px-2 py-1 bg-surface-elevated rounded"
            title={status.title}
        >
            <span
                aria-hidden="true"
                className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`}
            />
            {status.label}
        </span>
    );
}

function resolveStatus({ openCount, lastTradeTs, trades }) {
    if (openCount > 0) {
        return { label: "진입중", tone: "brand", title: "활성 포지션 보유" };
    }
    if (!lastTradeTs) {
        if (trades === 0) {
            return { label: "대기", tone: "muted", title: "아직 거래 발생 안 함" };
        }
        return { label: "활성", tone: "gain", title: "최근 거래 시각 미상" };
    }
    const idleHours = (Date.now() - new Date(lastTradeTs).getTime()) / 3600 / 1000;
    if (idleHours < IDLE_WARN_HOURS) {
        return { label: "활성", tone: "gain", title: `${idleHours.toFixed(1)}시간 전 거래` };
    }
    return { label: "휴면", tone: "warning", title: `${Math.floor(idleHours)}시간 idle` };
}

function formatUptime(firstTs) {
    if (!firstTs) return "—";
    const t = new Date(firstTs).getTime();
    if (isNaN(t)) return "—";
    const ms = Math.max(0, Date.now() - t);
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
}

function StrategyDescriptionCard({ meta, id }) {
    if (!meta) {
        return (
            <section className="bg-surface-card border border-surface-border rounded-lg p-4 mb-6">
                <div className="text-xs text-content-muted uppercase tracking-wide mb-2">전략 설명</div>
                <div className="text-sm text-content-secondary">
                    {id} 의 메타데이터가 등록되어 있지 않습니다.
                    <code className="ml-2 px-1 py-0.5 bg-surface-elevated rounded text-xs">
                        front_server/src/data/strategyMeta.js
                    </code>
                    에 추가하세요.
                </div>
            </section>
        );
    }
    return (
        <section
            className={`bg-surface-card border rounded-lg p-4 mb-6 ${
                meta.retired ? "border-loss/30" : "border-surface-border"
            }`}
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="text-xs text-content-muted uppercase tracking-wide">전략 설명</div>
                {meta.paradigm && (
                    <span className="text-xs text-content-secondary bg-surface-elevated px-2 py-0.5 rounded">
                        {meta.paradigm}
                    </span>
                )}
            </div>

            {meta.summary && (
                <p className="text-sm text-content leading-relaxed mb-3">{meta.summary}</p>
            )}

            <dl className="space-y-2 text-sm">
                {meta.mechanism && (
                    <div>
                        <dt className="text-xs text-content-muted mb-0.5">메커니즘</dt>
                        <dd className="text-content-secondary leading-relaxed">{meta.mechanism}</dd>
                    </div>
                )}
                {meta.entryExit && (
                    <div>
                        <dt className="text-xs text-content-muted mb-0.5">진입 / 청산</dt>
                        <dd className="text-content-secondary leading-relaxed">{meta.entryExit}</dd>
                    </div>
                )}
                {meta.retired && (
                    <div className="mt-3 pt-3 border-t border-surface-border">
                        <dt className="text-xs text-loss mb-0.5">
                            폐기됨
                            {meta.retiredAt && (
                                <span className="ml-1 text-content-muted">({meta.retiredAt})</span>
                            )}
                        </dt>
                        {meta.retiredReason && (
                            <dd className="text-content-secondary leading-relaxed">
                                {meta.retiredReason}
                            </dd>
                        )}
                        {meta.finalStats && (
                            <dd className="text-xs text-content-muted mt-1">
                                {Object.entries(meta.finalStats)
                                    .map(([k, v]) => `${k}: ${typeof v === "number" ? v.toLocaleString() : v}`)
                                    .join("  ·  ")}
                            </dd>
                        )}
                    </div>
                )}
            </dl>
        </section>
    );
}

function KpiCard({ label, value, tone = "neutral" }) {
    const toneClass =
        tone === "gain"
            ? "text-gain"
            : tone === "loss"
              ? "text-loss"
              : "text-content";
    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3">
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`text-base sm:text-lg font-semibold mt-1 ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}

const fmtKrwWon = (v) => `₩${Math.round(v).toLocaleString()}`;
const fmtSignedPct = (v) =>
    `${v > 0 ? "+" : v < 0 ? "-" : ""}${Math.abs(v).toFixed(1)}%`;
const fmtSignedKrw = (v) =>
    `${v > 0 ? "+" : v < 0 ? "-" : ""}${Math.round(Math.abs(v)).toLocaleString()} KRW`;

/**
 * CapitalPanel — 상세 페이지 전용 자본 현황 패널 (카드의 CapitalStat 확장판).
 *
 * 전체 화면 폭을 활용해 "시작 자본 → 남은 자본 → 수익률" 을 3분할로 크게 보여주고,
 * 그 아래 기준선 바로 본전(시작 자본) 위/아래를 한눈에 시각화한다. 모든 paper
 * 전략은 시작 자본 1천만 / 거래당 100만 KRW 로 균일 운영된다 (data/paperCapital.js).
 */
function CapitalPanel({ pnlKrw, initialCapital }) {
    const base = initialCapital != null ? initialCapital : PAPER_INITIAL_CAPITAL_KRW;
    const { initial, current, pnl, returnPct, fillPct } = computeCapital(pnlKrw, base);

    const tone = pnl > 0 ? "gain" : pnl < 0 ? "loss" : "neutral";
    const valueClass =
        tone === "gain" ? "text-gain" : tone === "loss" ? "text-loss" : "text-content";
    const barClass =
        tone === "gain" ? "bg-gain" : tone === "loss" ? "bg-loss" : "bg-content-muted";
    const accentBorder =
        tone === "gain"
            ? "border-l-gain"
            : tone === "loss"
              ? "border-l-loss"
              : "border-l-surface-border";
    const perTrade = initial / 10; // 거래당 = 시작 자본 / 10 (전략 공통 규칙)

    return (
        <section
            className={`bg-surface-card border border-surface-border border-l-2 ${accentBorder} rounded-lg p-4 mb-6`}
        >
            <div className="flex items-baseline justify-between gap-2 mb-3">
                <h2 className="text-base font-semibold text-content">자본 현황</h2>
                <span className="text-xs text-content-muted tabular-nums">
                    거래당 {fmtKrwWon(perTrade)}
                </span>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                    <div className="text-xs text-content-muted">시작 자본</div>
                    <div className="text-base sm:text-lg font-semibold text-content tabular-nums mt-0.5">
                        {fmtKrwWon(initial)}
                    </div>
                </div>
                <div>
                    <div className="text-xs text-content-muted">남은 자본</div>
                    <div
                        className={`text-base sm:text-lg font-bold tabular-nums mt-0.5 ${valueClass}`}
                    >
                        {fmtKrwWon(current)}
                    </div>
                </div>
                <div>
                    <div className="text-xs text-content-muted">수익률</div>
                    <div
                        className={`text-base sm:text-lg font-bold tabular-nums mt-0.5 ${valueClass}`}
                    >
                        {fmtSignedPct(returnPct)}
                    </div>
                </div>
            </div>

            {/* 기준선 바 — 중앙 세로선(본전) 기준 우측이면 이익, 좌측이면 손실 */}
            <div
                className="relative h-2 rounded-full bg-surface-elevated overflow-hidden"
                role="img"
                aria-label={`시작 자본 ${fmtKrwWon(initial)}, 현재 남은 자본 ${fmtKrwWon(
                    current,
                )}, 수익률 ${fmtSignedPct(returnPct)}`}
            >
                <div
                    className={`absolute inset-y-0 left-0 ${barClass} transition-[width] duration-500`}
                    style={{ width: `${fillPct}%` }}
                />
                <div
                    aria-hidden="true"
                    className="absolute inset-y-0 w-px bg-surface-card"
                    style={{ left: `${CAPITAL_BASELINE_PCT}%` }}
                />
            </div>
            <div className="flex justify-between mt-1.5 text-xs text-content-muted">
                <span>전손</span>
                <span>시작 자본 (본전)</span>
                <span>2× 시작</span>
            </div>

            <div className="mt-3 pt-3 border-t border-surface-border-light text-xs text-content-secondary tabular-nums">
                실현 손익 <span className={`font-semibold ${valueClass}`}>{fmtSignedKrw(pnl)}</span>
            </div>
        </section>
    );
}

/**
 * AdoptModal — 채택 사유 입력 모달. RetireModal과 동일한 구조를 따른다. (DAS-126)
 *
 * props:
 *   - id: 전략 ID (표시용)
 *   - onConfirm(reason): 사유를 전달하며 확정
 *   - onCancel(): 취소/닫기
 */
class AdoptModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = { reason: "" };
    }

    componentDidMount() {
        this.prevFocused =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        document.addEventListener("keydown", this.onKeyDown, true);
        if (this.reasonRef) this.reasonRef.focus();
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown, true);
        this.prevFocused?.focus?.();
    }

    onKeyDown = (e) => {
        if (e.key === "Escape") {
            this.props.onCancel?.();
            return;
        }
        if (e.key === "Tab") {
            const root = this.containerRef;
            if (!root) return;
            const focusables = Array.from(
                root.querySelectorAll(
                    'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
                ),
            ).filter((el) => !el.disabled && el.offsetParent !== null);
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    onConfirm = () => {
        const reason = this.state.reason.trim();
        if (!reason) return;
        this.props.onConfirm?.(reason);
    };

    render() {
        const { id, onCancel } = this.props;
        const canConfirm = this.state.reason.trim().length > 0;
        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-label={`${id} 전략 채택`}
            >
                {/* 백드롭 */}
                <button
                    type="button"
                    aria-label="닫기"
                    onClick={() => onCancel?.()}
                    className="absolute inset-0 bg-overlay cursor-default"
                />

                <div
                    ref={(el) => (this.containerRef = el)}
                    className="relative w-full max-w-lg bg-surface-card border border-surface-border rounded-xl shadow-xl max-h-full overflow-y-auto"
                >
                    <div className="p-4 sm:p-5">
                        <div className="flex items-start gap-2 mb-3">
                            <span
                                aria-hidden="true"
                                className="inline-block h-2 w-2 rounded-full bg-warning mt-1.5 shrink-0"
                            />
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-content">
                                    전략 채택 —{" "}
                                    <span className="font-mono">{id}</span>
                                </h2>
                                <p className="text-xs text-content-secondary mt-0.5">
                                    우수 전략으로 북마크합니다. 매매는 계속 유지되며,
                                    채택 배지와 사유가 카드에 표시됩니다.
                                </p>
                            </div>
                        </div>

                        <label className="flex flex-col gap-1 mb-4">
                            <span className="text-xs text-content-muted">
                                채택 사유 <span className="text-warning">*</span>
                            </span>
                            <textarea
                                ref={(el) => (this.reasonRef = el)}
                                value={this.state.reason}
                                onChange={(e) => this.setState({ reason: e.target.value })}
                                rows={3}
                                placeholder="예: 1주 paper net +1.2M KRW, 승률 62% — 실거래 전환 후보."
                                className="bg-surface border border-surface-border rounded px-2.5 py-2 text-sm text-content leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand resize-y"
                            />
                        </label>

                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => onCancel?.()}
                                className="px-3 py-2 text-sm text-content-secondary hover:text-content rounded min-h-[44px]"
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={this.onConfirm}
                                disabled={!canConfirm}
                                className="px-4 py-2 text-sm font-medium text-content-inverse bg-warning rounded min-h-[44px] disabled:opacity-50 hover:opacity-90 transition-opacity"
                            >
                                채택 확정
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default function PaperStrategyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    return <PaperStrategyDetailPageInner id={id} navigate={navigate} />;
}
